import { ArticleBusinessType } from 'admin/articleService'
import { CapacityDetails, CapacityHealth, CapacitySummary, CapacityWriteBody, NewCapacityBody } from 'capacity/types'

export interface CapacityMemberLink {
    productListId: string | null
    amountUsed: number
}

export interface CapacityMember {
    kind: 'product' | 'optionItem'
    id: string
    name: string
    businessType: ArticleBusinessType | null
    isBundle: boolean
    categoryIds: string[]
    inactiveCategoryIds: string[]
    links: CapacityMemberLink[]
    optionGroup?: { uuid: string; name: string }
}

export type OptionItemsCoverage = 'full' | 'capped' | 'failed'

export interface CapacityMembers {
    capacityId: string
    members: CapacityMember[]
    optionItems: OptionItemsCoverage
}

export type ProductOpKind = 'add' | 'remove' | 'setAmount'

export interface ProductOp {
    kind: ProductOpKind
    productId: string
    label: string
    amountUsed?: number
    expectedLinks?: CapacityMemberLink[]
}

export type CapacityWrite =
    | { kind: 'create'; body: NewCapacityBody }
    | { kind: 'update'; body: CapacityWriteBody }
    | null

export interface SavePlan {
    capacityId: string | null
    capacity: CapacityWrite
    productOps: ProductOp[]
}

export interface FailedOp {
    op: ProductOp
    message: string
}

export interface SaveResult {
    capacityId: string | null
    created: boolean
    capacitySaved: boolean
    succeeded: ProductOp[]
    failed: FailedOp[]
}

export type UsageBasis = 'peakSlice' | 'arrivals' | 'server'

export interface TodayUsage {
    status: 'ready' | 'unavailable'
    limit: number | null
    used: number | null
    left: number | null
    basis: UsageBasis
    timezone?: string
}

export interface GatewayCapabilities {
    members: 'clientJoin' | 'server'
    optionItemsCap: number | null
    atomicSave: boolean
    delete: boolean
    usage: 'peakSlice' | 'server' | 'none'
    health: 'server' | 'none'
}

export class CapacityNotSupportedError extends Error {
    constructor(public readonly ticket: string, message: string) {
        super(message)
        this.name = 'CapacityNotSupportedError'
    }
}

export interface CapacityGateway {
    readonly capabilities: GatewayCapabilities
    listCapacities(accountSlug: string): Promise<CapacitySummary[]>
    getCapacity(accountSlug: string, capacityId: string): Promise<CapacityDetails>
    getMembers(accountSlug: string, capacityId: string): Promise<CapacityMembers>
    save(accountSlug: string, plan: SavePlan): Promise<SaveResult>
    deleteCapacity(accountSlug: string, capacityId: string): Promise<void>
    getTodayUsage(accountSlug: string, capacityIds: string[], date: string): Promise<Record<string, TodayUsage>>
    getHealth(details: CapacityDetails): CapacityHealth
}
